import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageSEO } from '../hooks/usePageSEO';
import { breadcrumbSchema, serviceSchema, faqSchema } from '../utils/structuredData';
import { ArrowRightIcon, CheckCircle2Icon, StarIcon, AlertCircleIcon, SparklesIcon } from 'lucide-react';
import { SERVICES, getServiceBySlug } from '../utils/servicesData';
function StarRow() {
  return <div className="flex items-center gap-1" data-id="element-1430">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" data-id="element-1431" />)}
    </div>;
}
export function ServiceDetailPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const service = getServiceBySlug(slug);
  usePageSEO({
    canonical: `/services/${slug}`,
    keywords: service ? `${service.title.toLowerCase()}, Shopify ${service.title.toLowerCase()}, Shopify agency services, ecommerce ${service.title.toLowerCase()}` : 'Shopify services',
    jsonLd: service ? [breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Services',
      path: '/services'
    }, {
      name: service.title,
      path: `/services/${slug}`
    }]), serviceSchema({
      name: service.title,
      description: service.heroSubhead,
      path: `/services/${slug}`
    }), ...(service.faqs && service.faqs.length > 0 ? [faqSchema(service.faqs.map(f => ({
      question: f.q,
      answer: f.a
    })))] : [])] : undefined,
    title: service ? `${service.title} | Design Flow Digitals` : 'Services | Design Flow Digitals',
    description: service ? `${service.heroHeadline}. ${service.heroSubhead}` : 'Premium Shopify services by Design Flow Digitals.'
  });
  if (!service) {
    return <div className="pt-40 pb-32 px-6 text-center" data-id="element-1432">
        <h1 className="text-4xl font-bold mb-4" data-id="element-1433">Service not found</h1>
        <p className="text-gray-400 mb-8" data-id="element-1434">
          The service you’re looking for doesn’t exist.
        </p>
        <Link to="/services" className="inline-flex items-center gap-2 bg-gradient-gold text-black px-8 py-4 rounded-full font-bold gold-glow-hover transition-all hover:scale-105" data-id="element-1435">
          View all services <ArrowRightIcon className="w-5 h-5" data-id="element-1436" />
        </Link>
      </div>;
  }
  const Icon = service.icon;
  const otherServices = SERVICES.filter(s => s.slug !== service.slug);
  return <div className="w-full" data-id="element-1437">
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center pt-32 pb-20 overflow-hidden" data-id="element-1438">
        <div className="absolute inset-0 bg-grid-pattern opacity-10 pointer-events-none" data-id="element-1439" />
        <div className="absolute top-1/4 right-1/4 w-[34rem] h-[34rem] bg-[#D4AF37]/10 rounded-full blur-[130px] animate-pulse-glow pointer-events-none" data-id="element-1440" />

        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10 text-center flex flex-col items-center" data-id="element-1441">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 border-[#D4AF37]/30" data-id="element-1442">
            <SparklesIcon className="w-4 h-4 text-[#D4AF37]" data-id="element-1443" />
            <span className="text-sm font-semibold text-[#F3E5AB] tracking-wide uppercase" data-id="element-1444">
              {service.eyebrow}
            </span>
          </div>

          <div className="w-20 h-20 rounded-3xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-8" data-id="element-1445">
            <Icon className="w-10 h-10 text-[#D4AF37]" data-id="element-1446" />
          </div>

          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-1447">
            {service.heroHeadline}
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mb-8 leading-relaxed" data-id="element-1448">
            {service.heroSubhead}
          </p>

          <div className="flex items-center gap-3 mb-10" data-id="element-1449">
            <StarRow data-id="element-1450" />
            <span className="text-sm text-gray-400" data-id="element-1451">
              Trusted by 1,000+ Shopify merchants
            </span>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1452">
            <Link to="/contact" className="w-full sm:w-auto bg-gradient-gold text-black px-8 py-4 rounded-full font-bold text-lg gold-glow-hover transition-all duration-300 flex items-center justify-center gap-2 group hover:scale-105" data-id="element-1453">
              Start Your Project
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1454" />
            </Link>
            <Link to="/portfolio" className="w-full sm:w-auto px-8 py-4 rounded-full font-bold text-lg text-white border border-white/20 hover:bg-white/5 transition-colors" data-id="element-1455">
              See Our Work
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="border-y border-white/5 bg-[#050505]" data-id="element-1456">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-1457">
          {service.stats.map(s => <div key={s.label} className="text-center" data-id="element-1458">
              <p className="text-3xl md:text-4xl font-bold text-white mb-1" data-id="element-1459">
                {s.value}
              </p>
              <p className="text-sm text-gray-500" data-id="element-1460">{s.label}</p>
            </div>)}
        </div>
      </section>

      {/* Problem → Outcome */}
      <section className="section-padding bg-black" data-id="element-1461">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-1462">
          <div className="glass-card p-10 border-white/10" data-id="element-1463">
            <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6" data-id="element-1464">
              <AlertCircleIcon className="w-6 h-6 text-red-400" data-id="element-1465" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1466">The problem</h2>
            <p className="text-lg text-gray-400 leading-relaxed" data-id="element-1467">
              {service.problem}
            </p>
          </div>
          <div className="glass-card p-10 border-[#D4AF37]/20 bg-gradient-to-br from-[#D4AF37]/5 to-transparent" data-id="element-1468">
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-6" data-id="element-1469">
              <CheckCircle2Icon className="w-6 h-6 text-[#D4AF37]" data-id="element-1470" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4" data-id="element-1471">
              How we fix it
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed" data-id="element-1472">
              {service.outcome}
            </p>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-padding bg-[#020202] border-y border-white/5" data-id="element-1473">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1474">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1475">What’s included</h2>
          <p className="text-xl text-gray-400" data-id="element-1476">
            Everything you need, delivered to a premium standard.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-1477">
          {service.deliverables.map(d => <div key={d.title} className="glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-colors" data-id="element-1478">
              <CheckCircle2Icon className="w-6 h-6 text-[#D4AF37] mb-5" data-id="element-1479" />
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1480">{d.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1481">{d.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Benefits + Engagement */}
      <section className="section-padding bg-black" data-id="element-1482">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto" data-id="element-1483">
          <div data-id="element-1484">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1485">
              Why merchants choose us for {service.title.toLowerCase()}
            </h2>
            <ul className="space-y-4" data-id="element-1486">
              {service.benefits.map(b => <li key={b} className="flex items-start gap-3 text-gray-300" data-id="element-1487">
                  <CheckCircle2Icon className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" data-id="element-1488" />
                  <span className="text-lg" data-id="element-1489">{b}</span>
                </li>)}
            </ul>
          </div>

          <div className="glass-card p-10 border-[#D4AF37]/20 bg-gradient-to-b from-[#D4AF37]/5 to-transparent text-center" data-id="element-1490">
            <p className="text-sm font-bold text-[#D4AF37] uppercase tracking-widest mb-3" data-id="element-1491">
              {service.engagement.label}
            </p>
            <p className="text-4xl font-bold text-white mb-4" data-id="element-1492">
              {service.engagement.value}
            </p>
            <p className="text-gray-400 mb-8 leading-relaxed" data-id="element-1493">
              {service.engagement.note}
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 w-full bg-gradient-gold text-black px-8 py-4 rounded-full font-bold gold-glow-hover transition-all hover:scale-105" data-id="element-1494">
              Get a free proposal <ArrowRightIcon className="w-5 h-5" data-id="element-1495" />
            </Link>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding bg-[#020202] border-y border-white/5" data-id="element-1496">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1497">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1498">Our process</h2>
          <p className="text-xl text-gray-400" data-id="element-1499">
            A proven, transparent workflow from first call to lasting results.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-1500">
          {service.process.map(p => <div key={p.step} className="group glass-card p-8 border-white/10 relative overflow-hidden hover:border-[#D4AF37]/30 transition-colors" data-id="element-1501">
              <div className="text-5xl font-bold text-[#D4AF37]/15 mb-6 group-hover:text-[#D4AF37]/25 transition-colors" data-id="element-1502">
                {p.step}
              </div>
              <h3 className="text-lg font-bold text-white mb-3" data-id="element-1503">{p.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1504">{p.desc}</p>
            </div>)}
        </div>
      </section>

      {/* Testimonial */}
      <section className="section-padding bg-black" data-id="element-1505">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1506">
          <StarRow data-id="element-1507" />
          <p className="text-2xl md:text-3xl font-medium text-white leading-relaxed my-8" data-id="element-1508">
            “{service.testimonial.quote}”
          </p>
          <p className="font-bold text-white" data-id="element-1509">{service.testimonial.name}</p>
          <p className="text-sm text-gray-500" data-id="element-1510">{service.testimonial.role}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-[#020202] border-y border-white/5" data-id="element-1511">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1512">
          <h2 className="text-4xl font-bold mb-6" data-id="element-1513">
            Frequently asked questions
          </h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-5" data-id="element-1514">
          {service.faqs.map(f => <div key={f.q} className="glass-card p-6 border-white/10" data-id="element-1515">
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-1516">{f.q}</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-1517">{f.a}</p>
            </div>)}
        </div>
      </section>

      {/* Other services */}
      <section className="section-padding bg-black" data-id="element-1518">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-1519">
          <h2 className="text-3xl font-bold mb-4" data-id="element-1520">Explore more services</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-1521">
          {otherServices.map(s => {
          const OtherIcon = s.icon;
          return <Link key={s.slug} to={`/services/${s.slug}`} className="group glass-card p-8 border-white/10 hover:border-[#D4AF37]/30 transition-all hover:-translate-y-1" data-id="element-1522">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center mb-6" data-id="element-1523">
                  <OtherIcon className="w-6 h-6 text-[#D4AF37]" data-id="element-1524" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#D4AF37] transition-colors" data-id="element-1525">
                  {s.title}
                </h3>
                <span className="inline-flex items-center gap-1 text-sm text-[#D4AF37] font-medium" data-id="element-1526">
                  Learn more{' '}
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-1527" />
                </span>
              </Link>;
        })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 px-6 text-center bg-gradient-to-b from-transparent to-[#D4AF37]/5" data-id="element-1528">
        <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1529">
          Ready to get started?
        </h2>
        <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-1530">
          Book a free consultation and we’ll map out exactly how we’ll grow your
          Shopify store.
        </p>
        <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-gradient-gold text-black px-10 py-5 rounded-full font-bold text-lg gold-glow-hover transition-all duration-300 hover:scale-105" data-id="element-1531">
          Start Your Project <ArrowRightIcon className="w-5 h-5" data-id="element-1532" />
        </Link>
      </section>
    </div>;
}